/** @jsx jsx */
import React, { useCallback, useEffect, useMemo } from 'react';
import { jsx } from '@emotion/core';

import ActionButtonsContainer from 'components/ActionButtonsContainer';
import useTranslate, {
  useMarkdownTranslate,
  useMarkdownMultilineTranslate,
} from 'hooks/useTranslate/useTranslate';

import styles from './styles';
import { Divider } from '@material-ui/core';
import Button from 'components/Button';
import Link from 'components/Link';
import { helpText, composeStyles } from 'features/Theme';
import ExpandablePanel from 'components/ExpandablePanel';
import { FormikProps } from 'formik';
import { FormValues } from './UnifiedPrequalOffer';
import { conformToMask, decimalMoneyMask } from 'utils/masks';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import { selectTenant } from 'state/tenant';

import { useDispatch, useSelector } from 'react-redux';
import { selectPrequalOffer } from 'state/prequalOffer';
import { mergeApplication } from 'state/creditApplication';
import { selectAnalytics, setAnalyticsState } from 'state/analytics';

import useAnalytics, { Event } from 'hooks/useAnalytics';
import useAnalyticVariables from 'hooks/useAnalyticVariables';
import BreadTitle from '../OfferIntro/BreadTitle';

interface CreditCardPaymentOptionProps {
  formikProps: FormikProps<FormValues>;
  creditLimit?: number;
  panelName: string;
  expanded: boolean;
  nextPath: string;
  handlePanelExpandChange: (
    event: React.ChangeEvent<{}>,
    panelName: string,
    expanded: boolean,
  ) => void;
  intendedPurchaseAmount: number;
}

const CreditCard: React.FC<CreditCardPaymentOptionProps> = props => {
  const {
    creditLimit,
    panelName,
    expanded,
    handlePanelExpandChange,
    formikProps,
    intendedPurchaseAmount,
    nextPath,
  } = props;

  const { sendEvents } = useAnalytics();
  const tMd = useMarkdownTranslate();
  const tMdMultiline = useMarkdownMultilineTranslate();
  const t = useTranslate();

  const { setAnalyticsVariables } = useAnalyticVariables();

  useEffect(() => {
    expanded && sendEvents([Event.PrequalOfferExpanded]);
  }, [expanded, sendEvents, setAnalyticsVariables]);

  const { bread } = useSelector(selectTenant);
  if (!bread) {
    // Should never happens
    throw new Error('Invalid tenant config');
  }
  const creditLimitOrZero = creditLimit ? creditLimit : 0;

  const { applicationStarted } = useSelector(selectAnalytics);
  const totalDiscount = useMemo(() => {
    if (intendedPurchaseAmount && bread.prequalificationDiscount) {
      return bread.prequalificationDiscount * +intendedPurchaseAmount;
    }
    return undefined;
  }, [bread, intendedPurchaseAmount]);

  const percentageDiscount = useMemo(() => {
    if (bread.prequalificationDiscount) {
      return bread.prequalificationDiscount * 100;
    } else return undefined;
  }, [bread]);

  const dispatch = useDispatch();
  const prequalOffer = useSelector(selectPrequalOffer);

  const handleClick = useCallback(async () => {
    if (!applicationStarted) {
      // TODO: Check if sendEvents([Event.ApplicationStart]); is necessary
      sendEvents([Event.ApplicationStart]);
      dispatch(
        setAnalyticsState({
          applicationStarted: true,
        }),
      );
    }
    dispatch(
      mergeApplication({
        selectedOfferId: prequalOffer.prequalificationId,
        intendedPurchaseAmount: intendedPurchaseAmount.toString(),
      }),
    );
    formikProps.submitForm();
  }, [
    dispatch,
    sendEvents,
    applicationStarted,
    formikProps,
    intendedPurchaseAmount,
    prequalOffer.prequalificationId,
  ]);

  const banner = useMemo(() => {
    if (!creditLimit) {
      return undefined;
    }
    return tMd('unified.offer.paymentOption.creditCard.banner', {
      amount: creditLimit
        ? conformToMask(creditLimit.toString(), decimalMoneyMask)
        : '',
    });
  }, [creditLimit, tMd]);

  const capacityValueExceeded = useMemo(() => {
    return conformToMask(
      (intendedPurchaseAmount - creditLimitOrZero).toFixed(2),
      decimalMoneyMask,
    );
  }, [intendedPurchaseAmount, creditLimitOrZero]);

  const inError = useMemo(() => {
    return creditLimit && intendedPurchaseAmount > creditLimit;
  }, [creditLimit, intendedPurchaseAmount]);

  const bannerSecondaryText = useMemo(() => {
    return inError
      ? tMd('unified.offer.intendedPurchaseAmountMaximumError', {
          amount: capacityValueExceeded,
        })
      : null;
  }, [capacityValueExceeded, inError, tMd]);

  const handlePanelExpandChangeWhenNotError = useCallback(
    (event: React.ChangeEvent<{}>, panelName: string, expanded: boolean) => {
      if (!inError) {
        handlePanelExpandChange(event, panelName, expanded);
      }
    },
    [handlePanelExpandChange, inError],
  );

  const title = (
    <div>
      <div css={styles.paymentOption.summaryTitle}>
        <BreadTitle
          id={'creditCardBreadLogo'}
          titlePart={
            <React.Fragment>
              {tMd('unified.offer.paymentOption.creditCard.title')}
              {t('unified.creditCardLogo') !== '' && (
                <img
                  src={`data:image/png;base64,${t('unified.creditCardLogo')}`}
                  alt={t('unified.creditCardLogoAlt')}
                  css={styles.paymentOption.cardIcon}
                />
              )}
            </React.Fragment>
          }
          logo={t('unified.offer.paymentOption.creditCard.breadLogo')}
        />
      </div>
      {inError ? null : (
        <div css={styles.paymentOption.summaryBody}>
          {/*Get <span css={styles.boldColorText}>120.00 off</span> this purchase
        when you get 20% off your first Nebella purchase with the Nebella Credit
        Card*/}
          {totalDiscount && percentageDiscount
            ? tMd('unified.offer.paymentOption.creditCard.description', {
                percentDiscount: percentageDiscount,
                totalDiscount: conformToMask(
                  totalDiscount.toFixed(2),
                  decimalMoneyMask,
                ),
              })
            : tMd(
                'unified.offer.paymentOption.creditCard.descriptionWithoutDiscount',
              )}
        </div>
      )}
    </div>
  );
  const body = (
    <div css={styles.paymentOption.body}>
      <div css={styles.paymentOption.section}>
        <Divider />
      </div>
      <div css={styles.paymentOption.section}>
        <div>
          {totalDiscount && percentageDiscount
            ? tMdMultiline(
                'unified.offerIntro.paymentOption.creditCard.perks',
                {
                  percentDiscount: percentageDiscount,
                  totalDiscount: conformToMask(
                    totalDiscount.toFixed(2),
                    decimalMoneyMask,
                  ),
                },
              )
            : tMdMultiline(
                'unified.offerIntro.paymentOption.creditCard.perksWithoutDiscount',
              )}
        </div>
      </div>
      <div css={styles.paymentOption.section}>
        <Link
          id="creditCardLearnMore"
          to={'/unified/credit-card-offer/learn-more'}
          css={styles.chevronRightIcon}
          onClick={useCallback(async () => formikProps.submitForm(), [
            formikProps,
          ])}
        >
          {tMd('unified.offer.paymentOption.creditCard.learnMoreButton')}
          <ChevronRightIcon />
        </Link>
      </div>

      <div
        css={composeStyles([
          styles.paymentOption.section,
          styles.prequalMessage,
          helpText,
        ])}
      >
        {tMdMultiline('unified.offer.paymentOption.creditCard.prequalMessage')}
      </div>
    </div>
  );
  const footer = (
    <React.Fragment>
      <div
        css={composeStyles([
          styles.paymentOption.section,
          styles.paymentOption.footer,
        ])}
      >
        {tMdMultiline(
          'unified.offer.paymentOption.creditCard.hardInquiryMessage',
        )}
      </div>
      <ActionButtonsContainer>
        <Button
          mobileFullWidth={true}
          id="creditCardAcceptOffer"
          component={Link}
          disabled={!formikProps.isValid || formikProps.isSubmitting}
          onClick={handleClick}
          to={nextPath}
        >
          {tMd('unified.offer.paymentOption.creditCard.acceptButton')}
        </Button>
      </ActionButtonsContainer>
    </React.Fragment>
  );

  return (
    <ExpandablePanel
      expanded={inError ? false : expanded}
      hideToggleIcon={!!inError}
      title={title}
      children={body}
      footer={footer}
      panelName={panelName}
      onChange={handlePanelExpandChangeWhenNotError}
      banner={banner}
      bannerSecondaryText={bannerSecondaryText}
    />
  );
};

export default CreditCard;
